import { playerAnnouncements } from "@/lib/announce"

/**
 * The words for the two failures a visitor can hit while previewing or
 * downloading (docs/design-system/plate-and-hairline 09-Patterns §09.4 and
 * docs/ANTI_SLOP.md rule 5). One function per failure, so the sentence a screen
 * reader hears and the sentence on the page are the same sentence.
 */

/**
 * What a preview failure says, from the message `useAudioPlayer` raised.
 * Autoplay being blocked is the one cause the browser reports reliably, so it
 * gets its own sentence: nothing is broken, the browser is waiting for a tap.
 * Every other failure is cause-free. Chrome raises "format not supported" for
 * a 404 as well as for a real codec gap, so naming one would contradict the
 * download if that fails too.
 */
export function previewFailureMessage(playerError: string): string {
  if (/browser blocked audio playback/i.test(playerError)) {
    return playerAnnouncements.autoplayBlocked()
  }
  return "Preview did not load. Press play to try again, or download the file."
}

const HTTP_STATUS = /\(HTTP (\d{3})\)/

/**
 * What a failed download says. An HTTP status means our storage answered and
 * said no, so that much is known. A 429 is the per-visitor download limit
 * (60 a minute), so it says when to retry instead. Anything else (a `TypeError` from `fetch`
 * covers a dropped connection, a blocked request and a CORS failure alike) is
 * left cause-free.
 */
export function downloadFailureMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? "")
  const status = Number(HTTP_STATUS.exec(message)?.[1] ?? 0)
  if (status === 404) {
    return "This sound's file is missing from our storage. Pick another sound."
  }
  if (status === 429) {
    return "Download limit reached: too many downloads in the last minute. Wait a minute, then press Download again."
  }
  if (status >= 400) {
    return "Download failed: our file storage did not return the file. Press Download to try again."
  }
  return "Download failed: the file did not come through. Press Download to try again."
}

/**
 * What the USB swap pack says when every sound in it failed to fetch. Each
 * failure is reported on its own; which cause applied to which file is not
 * known here, so the sentence names the result rather than a reason.
 */
export function swapPackEmptyMessage(): string {
  return "None of the sound files came through, so there was nothing to zip. Press USB swap pack to try again."
}
